/**
 * Core-100 R2 — CORE-N: a reference's own answers, and how the owner asks for one.
 *
 * A response is the REFERENCE's words, attributed to its author and kept apart from the candidate's
 * record (the candidate can never write or edit one — the database refuses it). The owner may create a
 * one-time link and pass it on themselves; OptiBay sends nothing. A listed reference is never labelled
 * verified or confirmed — only a response, in their words, is shown as theirs.
 */
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

export interface ReferenceResponseRow {
  id: string;
  author_name: string;
  author_relationship: string | null;
  /** Core-100 R3: how the request reached the reference (OptiBay delivers nothing today). */
  delivery?: 'relayed_by_candidate' | 'delivered_by_optibay' | null;
  response: string;
  would_recommend: boolean | null;
  submitted_at: string;
}

export const REFERENCE_LINK_DAYS = [7, 14, 30] as const;

export function referenceResponseUrl(origin: string, token: string): string {
  // The token rides in the fragment, which browsers do not send to servers or put in referrers.
  return `${origin}/reference/respond#${token}`;
}

export function ReferenceResponses({ referenceId, isOwner }: { referenceId: string; isOwner: boolean }) {
  const qc = useQueryClient();
  const [link, setLink] = useState<string | null>(null);
  const [days, setDays] = useState<number>(14);
  const [error, setError] = useState<string | null>(null);
  const { data: responses } = useQuery({
    queryKey: ['reference-responses', referenceId],
    queryFn: async () => {
      const { data, error: err } = await supabase
        .from('reference_responses' as never)
        .select('id, author_name, author_relationship, response, would_recommend, submitted_at, delivery')
        .eq('reference_id', referenceId)
        .order('submitted_at', { ascending: false });
      if (err) return [] as ReferenceResponseRow[];
      return ((data ?? []) as unknown) as ReferenceResponseRow[];
    },
  });

  const { data: withdrawn } = useQuery({
    queryKey: ['reference-withdrawn', referenceId],
    queryFn: async () => {
      const { data } = await supabase.from('passport_references').select('subject_withdrew_at' as never).eq('id', referenceId).maybeSingle();
      return !!(data as { subject_withdrew_at?: string | null } | null)?.subject_withdrew_at;
    },
  });

  const createLink = async () => {
    setError(null);
    const { data, error: err } = await supabase.rpc('create_reference_response_link' as never, { p_reference_id: referenceId, p_valid_days: days } as never);
    if (err || typeof data !== 'string') { setError('The link could not be created.'); return; }
    setLink(referenceResponseUrl(window.location.origin, data));
    void qc.invalidateQueries({ queryKey: ['reference-responses', referenceId] });
  };

  return (
    <div className="mt-2 space-y-2">
      {withdrawn && (
        <p className="text-xs text-muted-foreground" data-testid="reference-withdrew">This person asked not to be listed as a reference; their contact details were removed.</p>
      )}
      {(responses ?? []).map((r) => (
        <blockquote key={r.id} className="rounded-md border-l-2 pl-3 text-sm" data-testid="reference-response">
          <p className="whitespace-pre-wrap">{r.response}</p>
          <footer className="mt-1 text-xs text-muted-foreground">
            In their own words — {r.author_name}{r.author_relationship ? `, ${r.author_relationship}` : ''} · {new Date(r.submitted_at).toLocaleDateString()}
            {r.would_recommend === true ? ' · would recommend' : r.would_recommend === false ? ' · would not recommend' : ''}
            {/* Core-100 R3: OptiBay did not deliver the request — the candidate passed the link on. Said, not hidden. */}
            {r.delivery !== 'delivered_by_optibay' && (
              <span className="block" data-testid="reference-relayed">The request was passed on by the candidate; OptiBay did not deliver it.</span>
            )}
          </footer>
        </blockquote>
      ))}
      {isOwner && !withdrawn && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <label className="flex items-center gap-1">
            Link valid for
            <select className="rounded border bg-background px-1 py-0.5" value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Link valid for">
              {REFERENCE_LINK_DAYS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </label>
          <Button variant="outline" size="sm" onClick={() => void createLink()}>Ask for a response</Button>
          {link && (
            <span className="w-full break-all" data-testid="reference-link">
              Send this link to them yourself (it works once): <code>{link}</code>
            </span>
          )}
          {error && <span role="alert" className="text-destructive">{error}</span>}
        </div>
      )}
    </div>
  );
}
